import { useMemo } from 'react';

import { useJobContext } from 'pages/Job/context';
import { formatToCurrency, formatToPercentage } from 'utils';
import {
  getFinanceRateMaximumAllowedDepositValue,
  getMinimumMonthlyForRate,
  getRateWithMinimumMonthly,
  getTotalLoanCostForRate,
} from 'utils/FinanceRate-functions';

import { INITIAL, SHOWCASE } from './constants';

export interface FinanceHelperHook {
  text: string | undefined;
}

export default function useFinanceHelper(): FinanceHelperHook {
  const { job, allRates } = useJobContext();

  const minimumMonthlyRate = useMemo(
    () => getRateWithMinimumMonthly(job, allRates),
    [job, allRates],
  );

  const text = useMemo(() => {
    if (!job || !minimumMonthlyRate) return undefined;
    const total = job.total?.amount_vat_inclusive?.amount;
    if (!total) return undefined;
    const deposit = job.deposit_value.amount;
    const depositType = job.deposit_type;
    const maximumAllowedDeposit =
      depositType === 'no_deposit'
        ? getFinanceRateMaximumAllowedDepositValue(total, minimumMonthlyRate)
        : deposit;
    const minimumMonthly = getMinimumMonthlyForRate(
      minimumMonthlyRate,
      total,
      deposit,
      depositType === 'no_deposit',
    );
    const loanCost = getTotalLoanCostForRate(
      minimumMonthlyRate,
      total,
      deposit,
      depositType === 'no_deposit',
    );
    const loan = total - maximumAllowedDeposit;
    const apr = minimumMonthlyRate.apr ?? 0;
    const duration = minimumMonthlyRate.duration ?? 1;
    const aprType = minimumMonthlyRate.apr_type ?? 'INTEREST_FREE';
    const main = SHOWCASE[aprType];
    const initial =
      depositType === 'no_deposit'
        ? INITIAL.changeDeposit
        : INITIAL.fixedDeposit;
    if (aprType === 'BUYNOW_PAYLATER') {
      const deferred = minimumMonthlyRate?.deferred_duration ?? 1;
      return [
        initial,
        main
          .replaceAll('{{minimumMonthly}}', formatToCurrency(minimumMonthly))
          .replaceAll('{{loan}}', formatToCurrency(loan))
          .replaceAll('{{deferred}}', deferred.toString())
          .replaceAll('{{duration}}', duration.toString())
          .replaceAll('{{apr}}', formatToPercentage(apr))
          .replaceAll('{{loanCost}}', formatToCurrency(loanCost)),
      ].join(' ');
    }
    return [
      initial,
      main
        .replaceAll('{{minimumMonthly}}', formatToCurrency(minimumMonthly))
        .replaceAll('{{loan}}', formatToCurrency(loan))
        .replaceAll('{{duration}}', duration.toString())
        .replaceAll('{{apr}}', formatToPercentage(apr))
        .replaceAll('{{loanCost}}', formatToCurrency(loanCost)),
    ].join(' ');
  }, [minimumMonthlyRate, job]);

  return {
    text,
  };
}
